import { Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'

export default function ConversationShow({ conversation, messages = [], patient, currentUser }) {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">
          <h1 className="text-xl font-bold">Live Triage</h1>
          <div className="flex items-center gap-4">
            <Link href="/dashboard"><Button variant="ghost" size="sm">Dashboard</Button></Link>
            <span className="text-sm text-muted-foreground">{currentUser?.email}</span>
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-5xl px-4 py-10">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold tracking-tight">Conversation {conversation.uuid.slice(0, 8)}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Agent: <span className="font-medium">{conversation.agent_name}</span> · Status: <span className="font-medium">{conversation.status}</span>
            {conversation.model_used && <> · Model: <span className="font-mono text-xs">{conversation.model_used}</span></>}
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
          <section className="rounded-lg border bg-card">
            <div className="border-b px-5 py-3">
              <h3 className="text-sm font-semibold tracking-tight">Transcript ({messages.length} message{messages.length === 1 ? '' : 's'})</h3>
            </div>
            {messages.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-muted-foreground">No messages logged yet.</div>
            ) : (
              <ul className="divide-y">
                {messages.map((m) => (
                  <li key={m.id} className="px-5 py-3">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className={`text-xs font-medium uppercase tracking-wide ${roleColor(m.role)}`}>{m.role}{m.tool_name ? ` · ${m.tool_name}` : ''}</span>
                      <span className="text-xs text-muted-foreground">{formatTime(m.occurred_at)}</span>
                    </div>
                    {m.content && <p className="mt-1 text-sm">{m.content}</p>}
                    {m.tool_args && (
                      <pre className="mt-2 overflow-x-auto rounded bg-muted px-2 py-1.5 text-xs">{JSON.stringify(m.tool_args, null, 2)}</pre>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <aside className="space-y-4">
            {patient && (
              <>
                <section className={`rounded-lg border p-5 ${patient.urgent ? 'border-red-600/40 bg-red-50' : 'bg-card'}`}>
                  <h3 className="text-sm font-semibold tracking-tight">Patient</h3>
                  <p className="mt-2 text-sm">{[patient.first_name, patient.last_name].filter(Boolean).join(' ') || '—'}</p>
                  {patient.estimated_esi_score && (
                    <p className="mt-1 text-xs">ESI {patient.estimated_esi_score}{patient.urgent ? ' (urgent)' : ''}</p>
                  )}
                  {patient.esi_reason && <p className="mt-2 text-xs text-muted-foreground">{patient.esi_reason}</p>}
                  <div className="mt-3">
                    <Link href={`/patients/${patient.id}`}>
                      <Button size="sm" variant="outline">View full patient</Button>
                    </Link>
                  </div>
                </section>
                {patient.recording_url && (
                  <section className="overflow-hidden rounded-lg border bg-black">
                    <video src={patient.recording_url} controls className="aspect-video w-full" />
                  </section>
                )}
              </>
            )}
          </aside>
        </div>
      </main>
    </div>
  )
}

function roleColor(role) {
  switch (role) {
    case 'user': return 'text-blue-700'
    case 'assistant': return 'text-emerald-700'
    case 'tool_call': return 'text-purple-700'
    case 'tool_result': return 'text-purple-500'
    default: return 'text-muted-foreground'
  }
}

function formatTime(value) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' })
}
